/**
 * World pack v1: a generated area as one file, built once per course on the server and loaded
 * by every viewer instead of reading the terrain datasets in the browser
 * (docs/features/play-world-events.md, docs/features/play-world-osm.md §3). These functions read
 * and write the unpacked bytes; the file on disk and over the wire is them gzipped.
 *
 * Layout, every number little-endian:
 *
 *   0       4   magic "SBWP"
 *   4       2   format version (uint16)
 *   6       2   reserved, 0
 *   8       4   header length H (uint32), a multiple of 4
 *   12      H   header: UTF-8 JSON, padded with spaces to H bytes
 *   12 + H  …   the arrays, at the offsets the header gives (multiples of 4, counted from 0)
 *
 * Heights are uint16 (metres = offsetM + value × scaleM); terrain types are uint8 codes from
 * `landcover.ts`. Both grids are row-major from the north-west corner, as in `GeneratedTerrain`.
 * Slope is derived again on reading.
 *
 * Optional sections, which a reader that doesn't know them skips, come after the types; each is
 * named in the header, so adding one needs no new version (changing one does). OpenStreetMap
 * (docs/features/play-world-osm.md §3) brings three, left out when none was read:
 *
 * - `refinedTypes`: the type codes only OpenStreetMap can tell (10–13: sand, vineyard, orchard,
 *   lawn), 0 elsewhere, on the types grid. `types` holds each of them as its nearest WorldCover
 *   type (`BASE_TYPE_OF`), so a reader from before OpenStreetMap still draws a whole world;
 * - `newerTypes`: codes after those (14: scrub), the same way, so a reader that knows only 1–13
 *   draws their base type too (it applies every code in `refinedTypes`, known or not);
 * - `ways`: roads, tracks, paths and streams (`ways.ts`);
 * - `blocks`: where the buildings are, a sparse grid of built cells with a height class each (`blocks.ts`);
 * - the header's `osm`: where they came from.
 *
 * The header's `biome` says what grows there when it isn't the default (`detectBiome`).
 */

import type { GeneratedTerrain, TerrainOsm, TerrainSummary } from './generate'
import { slopeDegrees, type Grid, type GridSpec } from './grid'
import { BlocksFormatError, blockCount, decodeBlocks, encodeBlocks } from './blocks'
import { BASE_TYPE_CODES, BASE_TYPE_OF, REFINED_TYPE_CODES, TERRAIN_CODE, type Biome } from './landcover'
import { decodeWays, encodeWays, WaysFormatError } from './ways'
import type { LngLatBounds } from '@/lib/virtual-course/projection'

export const WORLD_PACK_VERSION = 1
const MAGIC = 'SBWP'
const PREAMBLE = 12
const MAX_U16 = 65535

export class WorldPackError extends Error {}

interface ArrayEntry {
  byteOffset: number
  byteLength: number
}

interface WorldPackHeader {
  bounds: LngLatBounds
  summary: TerrainSummary
  attribution: string[]
  missingTiles: string[]
  heights: GridSpec & ArrayEntry & { offsetM: number; scaleM: number }
  types: GridSpec & ArrayEntry
  /** With OpenStreetMap: its own type codes on the types grid, 0 where `types` says it all. */
  refinedTypes?: ArrayEntry
  /** Type codes after the refined ones, the same way. */
  newerTypes?: ArrayEntry
  /** With OpenStreetMap: the ways' bytes. */
  ways?: ArrayEntry & { count: number }
  /** With OpenStreetMap: the built blocks' grid and bytes. */
  blocks?: GridSpec & ArrayEntry & { count: number }
  osm?: TerrainOsm
  biome?: Biome
}

/** Code → the code a reader of nine types draws it as. */
const BASE_CODE = new Uint8Array(256).map((_, code) => code)
for (const [type, base] of Object.entries(BASE_TYPE_OF)) BASE_CODE[TERRAIN_CODE[type as keyof typeof BASE_TYPE_OF]] = TERRAIN_CODE[base]

/** The types as every reader reads them, OpenStreetMap's first codes, and the newer ones (null when there are none). */
function splitTypes(values: Uint8Array): { base: Uint8Array; refined: Uint8Array | null; newer: Uint8Array | null } {
  if (!values.some((code) => code > BASE_TYPE_CODES)) return { base: values, refined: null, newer: null }
  const base = new Uint8Array(values.length)
  const refined = new Uint8Array(values.length)
  const newer = new Uint8Array(values.length)
  let anyRefined = false
  let anyNewer = false
  for (let index = 0; index < values.length; index++) {
    const code = values[index]
    base[index] = BASE_CODE[code]
    if (code > REFINED_TYPE_CODES) {
      newer[index] = code
      anyNewer = true
    } else if (code > BASE_TYPE_CODES) {
      refined[index] = code
      anyRefined = true
    }
  }
  return { base, refined: anyRefined ? refined : null, newer: anyNewer ? newer : null }
}

const align4 = (n: number) => (n + 3) & ~3

function gridSpec<T extends ArrayLike<number>>({ bounds, cols, rows, cellM, widthM, heightM }: Grid<T>): GridSpec {
  return { bounds, cols, rows, cellM, widthM, heightM }
}

export function encodeWorldPack(terrain: GeneratedTerrain): Uint8Array {
  const heights = terrain.heights.values
  let min = Infinity
  let max = -Infinity
  for (const value of heights) {
    if (value < min) min = value
    if (value > max) max = value
  }
  const offsetM = Number.isFinite(min) ? min : 0
  const scaleM = max > min ? (max - min) / MAX_U16 : 1
  const quantised = new Uint16Array(heights.length)
  for (let i = 0; i < heights.length; i++) quantised[i] = Math.round((heights[i] - offsetM) / scaleM)
  const { base: types, refined, newer } = splitTypes(terrain.types.values)
  const ways = terrain.ways ? encodeWays(terrain.ways, terrain.heights.widthM, terrain.heights.heightM) : null
  const blocks = terrain.blocks ? encodeBlocks(terrain.blocks) : null

  // The header's length fixes the array offsets, which the header holds: lay out with room for
  // the offsets' digits, then pad the JSON to the length it was laid out for.
  const draft = (headerLength: number): WorldPackHeader => {
    const heightsOffset = align4(PREAMBLE + headerLength)
    const typesOffset = align4(heightsOffset + quantised.byteLength)
    const refinedOffset = align4(typesOffset + types.byteLength)
    const newerOffset = align4(refinedOffset + (refined?.byteLength ?? 0))
    const waysOffset = align4(newerOffset + (newer?.byteLength ?? 0))
    const blocksOffset = align4(waysOffset + (ways?.byteLength ?? 0))
    return {
      bounds: terrain.bounds,
      summary: terrain.summary,
      attribution: terrain.attribution,
      missingTiles: terrain.missingTiles,
      heights: { ...gridSpec(terrain.heights), offsetM, scaleM, byteOffset: heightsOffset, byteLength: quantised.byteLength },
      types: { ...gridSpec(terrain.types), byteOffset: typesOffset, byteLength: types.byteLength },
      ...(refined && { refinedTypes: { byteOffset: refinedOffset, byteLength: refined.byteLength } }),
      ...(newer && { newerTypes: { byteOffset: newerOffset, byteLength: newer.byteLength } }),
      ...(ways && terrain.ways && { ways: { byteOffset: waysOffset, byteLength: ways.byteLength, count: terrain.ways.classes.length } }),
      ...(blocks &&
        terrain.blocks && { blocks: { ...gridSpec(terrain.blocks), byteOffset: blocksOffset, byteLength: blocks.byteLength, count: blockCount(terrain.blocks) } }),
      ...(terrain.osm && { osm: terrain.osm }),
      ...(terrain.biome && { biome: terrain.biome }),
    }
  }
  const encoder = new TextEncoder()
  const headerLength = align4(encoder.encode(JSON.stringify(draft(1 << 30))).length)
  const header = draft(headerLength)
  const json = encoder.encode(JSON.stringify(header))

  const last = header.blocks ?? header.ways ?? header.newerTypes ?? header.refinedTypes ?? header.types
  const bytes = new Uint8Array(last.byteOffset + last.byteLength)
  bytes.set(encoder.encode(MAGIC), 0)
  const view = new DataView(bytes.buffer)
  view.setUint16(4, WORLD_PACK_VERSION, true)
  view.setUint16(6, 0, true)
  view.setUint32(8, headerLength, true)
  bytes.fill(0x20, PREAMBLE, PREAMBLE + headerLength)
  bytes.set(json, PREAMBLE)
  for (let i = 0; i < quantised.length; i++) view.setUint16(header.heights.byteOffset + i * 2, quantised[i], true)
  bytes.set(types, header.types.byteOffset)
  if (refined && header.refinedTypes) bytes.set(refined, header.refinedTypes.byteOffset)
  if (newer && header.newerTypes) bytes.set(newer, header.newerTypes.byteOffset)
  if (ways && header.ways) bytes.set(ways, header.ways.byteOffset)
  if (blocks && header.blocks) bytes.set(blocks, header.blocks.byteOffset)
  return bytes
}

export function decodeWorldPack(bytes: Uint8Array): GeneratedTerrain {
  if (bytes.byteLength < PREAMBLE || new TextDecoder().decode(bytes.subarray(0, 4)) !== MAGIC) {
    throw new WorldPackError('Not a world pack')
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const version = view.getUint16(4, true)
  if (version !== WORLD_PACK_VERSION) throw new WorldPackError(`World pack version ${version} is not one this reads`)
  const headerLength = view.getUint32(8, true)
  if (PREAMBLE + headerLength > bytes.byteLength) throw new WorldPackError('World pack header length runs past the file')

  let header: WorldPackHeader
  try {
    header = JSON.parse(new TextDecoder().decode(bytes.subarray(PREAMBLE, PREAMBLE + headerLength)))
  } catch {
    throw new WorldPackError('World pack header is not JSON')
  }
  for (const [name, entry, size] of [
    ['heights', header.heights, 2],
    ['types', header.types, 1],
  ] as const) {
    if (entry.byteLength !== entry.cols * entry.rows * size || entry.byteOffset + entry.byteLength > bytes.byteLength) {
      throw new WorldPackError(`World pack ${name} length does not match its grid`)
    }
  }

  const { offsetM, scaleM, byteOffset: heightsOffset, byteLength: _heightsLength, ...heightSpec } = header.heights
  const heights: Grid<Float32Array> = { ...heightSpec, values: new Float32Array(heightSpec.cols * heightSpec.rows) }
  for (let i = 0; i < heights.values.length; i++) heights.values[i] = offsetM + view.getUint16(heightsOffset + i * 2, true) * scaleM
  const { byteOffset: typesOffset, byteLength: typesLength, ...typeSpec } = header.types
  const types: Grid<Uint8Array> = { ...typeSpec, values: bytes.slice(typesOffset, typesOffset + typesLength) }
  for (const [name, entry] of [
    ['refined', header.refinedTypes],
    ['newer', header.newerTypes],
  ] as const) {
    if (!entry) continue
    const { byteOffset, byteLength } = entry
    if (byteLength !== typesLength || byteOffset + byteLength > bytes.byteLength) throw new WorldPackError(`World pack ${name} types do not match its grid`)
    for (let index = 0; index < byteLength; index++) {
      const code = bytes[byteOffset + index]
      if (code) types.values[index] = code
    }
  }

  let ways: GeneratedTerrain['ways']
  if (header.ways) {
    const { byteOffset, byteLength } = header.ways
    if (byteOffset + byteLength > bytes.byteLength) throw new WorldPackError('World pack ways run past the file')
    try {
      ways = decodeWays(bytes.subarray(byteOffset, byteOffset + byteLength), heights.widthM, heights.heightM)
    } catch (error) {
      throw error instanceof WaysFormatError ? new WorldPackError(error.message) : error
    }
  }

  let blocks: GeneratedTerrain['blocks']
  if (header.blocks) {
    const { byteOffset, byteLength, count: _count, ...blockSpec } = header.blocks
    if (byteOffset + byteLength > bytes.byteLength) throw new WorldPackError('World pack blocks run past the file')
    try {
      blocks = decodeBlocks(bytes.subarray(byteOffset, byteOffset + byteLength), blockSpec)
    } catch (error) {
      throw error instanceof BlocksFormatError ? new WorldPackError(error.message) : error
    }
  }

  return {
    bounds: header.bounds,
    heights,
    slope: slopeDegrees(heights),
    types,
    summary: header.summary,
    attribution: header.attribution,
    missingTiles: header.missingTiles,
    ...(ways && { ways }),
    ...(blocks && { blocks }),
    ...(header.osm && { osm: header.osm }),
    ...(header.biome && { biome: header.biome }),
  }
}

/** A pack the server built (the viewer payload's `world_url`): fetched, unzipped in the browser and read. */
export async function fetchWorldPack(url: string, signal?: AbortSignal): Promise<GeneratedTerrain> {
  const response = await fetch(url, { signal })
  if (!response.ok || !response.body) throw new WorldPackError(`World pack fetch ${response.status}`)
  const unzipped = response.body.pipeThrough(new DecompressionStream('gzip'))
  return decodeWorldPack(new Uint8Array(await new Response(unzipped).arrayBuffer()))
}
